import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { fpBase, inr, monthLabel, shortInr, useFpContext } from "@/lib/fp";
import { CheckCircle, DownloadSimple, Lock, LockOpen, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const TONE = {
  draft: "bg-muted text-muted-foreground",
  approved: "bg-success/15 text-[hsl(152_60%_30%)]",
};

const Cell = ({ value, onChange, disabled, testid }) => (
  <input value={value ?? ""} disabled={disabled} data-testid={testid}
         onChange={(e) => onChange(e.target.value)}
         className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-xs text-navy outline-none hover:border-border focus:border-terracotta focus:bg-card disabled:opacity-70" />
);

const NumCell = ({ value, onChange, disabled, testid }) => (
  <input type="number" step="any" value={value ?? ""} disabled={disabled} data-testid={testid}
         onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
         className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-right font-mono text-xs tabular-nums text-navy outline-none hover:border-border focus:border-terracotta focus:bg-card disabled:opacity-70" />
);

const FnCell = ({ value, options, onChange, disabled, testid }) => {
  const [typing, setTyping] = useState(false);
  const opts = useMemo(() => {
    const list = [...(options || [])];
    if (value && !list.some((o) => o.toLowerCase() === String(value).toLowerCase()))
      list.push(value);
    return list;
  }, [options, value]);
  if (typing && !disabled) {
    return (
      <input autoFocus value={value ?? ""} data-testid={testid}
             onChange={(e) => onChange(e.target.value)}
             onBlur={(e) => {
               const v = e.target.value.trim();
               if (!v || opts.some((o) => o.toLowerCase() === v.toLowerCase())) setTyping(false);
             }}
             onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
             placeholder="Type the new function head"
             className="w-full rounded-md border border-terracotta bg-card px-2 py-1 text-xs text-navy outline-none" />
    );
  }
  return (
    <select value={value ?? ""} disabled={disabled} data-testid={testid}
            onChange={(e) => {
              if (e.target.value === "__new__") setTyping(true);
              else onChange(e.target.value);
            }}
            className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-xs text-navy outline-none hover:border-border focus:border-terracotta focus:bg-card disabled:opacity-70">
      <option value="">— Unallocated —</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      {!disabled && <option value="__new__">＋ New function head…</option>}
    </select>
  );
};

const DeltaBadge = ({ value }) => {
  if (!value || Math.abs(value) < 0.005) return null;
  const up = value > 0;
  return (
    <span className={`ml-2 text-[10px] font-bold ${up ? "text-[hsl(152_60%_30%)]" : "text-terracotta"}`}>
      {up ? "▲" : "▼"} {shortInr(Math.abs(value))}
    </span>
  );
};

const IMPACT_LINES = [
  { key: "throughput", label: "Throughput" },
  { key: "throughput_pct", label: "Throughput %", pct: true },
  { key: "fixed_expenses", label: "Fixed cost" },
  { key: "ebitda", label: "EBITDA" },
];

const SUMMARY_LINES = [
  { key: "revenue", label: "Revenue" },
  { key: "pvc", label: "Production variable cost" },
  { key: "dvc", label: "Distribution variable cost" },
  { key: "variable_cost", label: "Variable cost", bold: true },
  { key: "throughput", label: "Throughput", bold: true },
  { key: "throughput_pct", label: "Throughput %", pct: true },
  { key: "fixed_expenses", label: "Fixed cost" },
  { key: "ebitda", label: "EBITDA (operating profit)", bold: true },
  { key: "ebitda_margin", label: "EBITDA %", pct: true },
];

const fmtLine = (line, v) =>
  v == null ? "—" : line.pct ? `${Number(v).toFixed(1)}%` : inr(v);

// ---------- how the area's figure was assembled: formula, its parts, the stock bridges ----------
const BridgeCard = ({ bridge, label, open, onToggle }) => {
  if (!bridge) return null;
  const { steps, stock, adjustment, total, steps_total: stepsTotal } = bridge;
  return (
    <div className="panel overflow-hidden" data-testid="fp-lr-bridge">
      <button onClick={onToggle} data-testid="fp-lr-bridge-toggle"
              className="flex w-full items-start justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-muted/30">
        <div className="min-w-0">
          <p className="label-caps">How {label.toLowerCase()} is calculated</p>
          <p className="mt-1 font-mono text-xs leading-relaxed text-navy">{bridge.formula}</p>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Arrives at <span className="font-mono font-semibold text-navy">{inr(total)}</span> —
            the figure Explorer and the Financial Model read. {open ? "" : "Tap to see every part."}
          </p>
        </div>
        <span className="mt-1 shrink-0 text-[10px] font-bold uppercase tracking-[0.12em] text-terracotta">
          {open ? "Hide" : "Explain"}
        </span>
      </button>
      {open && (
        <div className="space-y-5 border-t border-border px-6 py-5" data-testid="fp-lr-bridge-body">
          {stock.map((s) => (
            <div key={s.label} data-testid={`fp-lr-bridge-stock-${s.label}`}>
              <p className="label-caps">{s.label} — the stock bridge</p>
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs">
                {s.parts.map((p, i) => (
                  <span key={p.label} className="whitespace-nowrap">
                    {i > 0 && <span className="mr-2 text-muted-foreground">{p.amount < 0 ? "−" : "+"}</span>}
                    <span className="tabular-nums text-navy">{inr(Math.abs(p.amount))}</span>
                    <span className="ml-1 text-[10px] text-muted-foreground">{p.label}</span>
                  </span>
                ))}
                <span className="whitespace-nowrap">
                  <span className="mr-2 text-muted-foreground">=</span>
                  <span className="font-semibold tabular-nums text-terracotta">{inr(s.total)}</span>
                </span>
              </div>
            </div>
          ))}
          <div>
            <p className="label-caps">Built up by {bridge.split}</p>
            <div className="mt-2 space-y-1">
              {steps.map((s) => (
                <div key={s.label} className="flex items-baseline justify-between gap-4 text-xs"
                     data-testid={`fp-lr-bridge-step-${s.label}`}>
                  <span className="text-navy">{s.label}</span>
                  <span className="font-mono tabular-nums text-muted-foreground">{inr(s.amount)}</span>
                </div>
              ))}
              {!!adjustment && (
                <div className="flex items-baseline justify-between gap-4 text-xs text-terracotta"
                     data-testid="fp-lr-bridge-adjustment">
                  <span>Sheet adjustments reflected in the total</span>
                  <span className="font-mono tabular-nums">{inr(adjustment)}</span>
                </div>
              )}
              <div className="flex items-baseline justify-between gap-4 border-t border-border pt-1.5 text-xs font-semibold">
                <span className="text-navy">{label}</span>
                <span className="font-mono tabular-nums text-navy" data-testid="fp-lr-bridge-total">
                  {inr(total)}
                </span>
              </div>
              {Math.abs(stepsTotal + adjustment - total) > 1 && (
                <p className="pt-1 text-[10px] text-muted-foreground">
                  The parts shown are the top contributors; smaller ones are folded into the total.
                </p>
              )}
            </div>
          </div>
          <div>
            <p className="label-caps">Read from</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {bridge.sources.map((s) => (
                <span key={s} className="rounded-full bg-muted px-2.5 py-1 text-[10px] text-muted-foreground">
                  {s}
                </span>
              ))}
            </div>
          </div>
          <p className="text-[11px] leading-relaxed text-muted-foreground">{bridge.note}</p>
        </div>
      )}
    </div>
  );
};

export default function FpLineReview() {
  const { tenantId, period } = useFpContext();
  const [data, setData] = useState(null);
  const [area, setArea] = useState("revenue");
  const [draft, setDraft] = useState({});   // key -> edited fields, unsaved
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [bridgeOpen, setBridgeOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !period) return;
    try {
      const { data: out } = await api.get(`${fpBase(tenantId)}/review-sheets`,
                                          { params: { period } });
      setData(out);
      setDraft({});
    } catch (e) { toast.error(errMsg(e)); }
  }, [tenantId, period]);

  useEffect(() => { load(); }, [load]);

  const isSummary = area === "summary";
  const current = isSummary ? null : data?.areas?.[area];
  const locked = current?.status === "approved";
  const canEdit = data?.can_edit && !locked && !isSummary;

  const rows = useMemo(() => {
    const base = current?.rows || [];
    return base.map((r) => draft[r.key] ? { ...r, ...draft[r.key], edited: true } : r);
  }, [current, draft]);

  // the grid holds two views of the same money — P&L ledger lines (the books) and register
  // detail lines (operations) — shown as separate subtotals, never one combined sum
  const booksLines = useMemo(
    () => rows.reduce((s, r) => s + (!r.detail && !r.excluded ? Number(r.amount) || 0 : 0), 0),
    [rows]);
  const registerLines = useMemo(
    () => rows.reduce((s, r) => s + (r.detail && !r.excluded ? Number(r.amount) || 0 : 0), 0),
    [rows]);
  const editedCount = rows.filter((r) => r.edited).length;

  // the grid reads as a calculation: lines grouped into the accounting step they belong to
  const grouped = useMemo(() => {
    const stages = current?.stages || [];
    return stages.map((s) => {
      const items = rows.filter((r) => (r.excluded ? "excluded" : r.stage) === s.key);
      return { ...s, rows: items,
               total: items.reduce((t, r) => t + (r.excluded ? 0 : Number(r.amount) || 0), 0) };
    }).filter((g) => g.rows.length);  }, [rows, current]);

  // live impact preview: the full model re-simulated as if every edited sheet were approved
  const sheetsPayload = useMemo(() => {
    if (!data) return null;
    const out = {};
    for (const a of data.area_order) {
      const base = data.areas[a]?.rows || [];
      out[a] = {
        version: data.areas[a]?.version || 1,
        rows: a === area
          ? base.map((r) => draft[r.key] ? { ...r, ...draft[r.key], edited: true } : r)
          : base,
      };
    }
    return out;
  }, [data, area, draft]);

  const anyEdited = useMemo(
    () => !sheetsPayload ? 0
        : Object.values(sheetsPayload)
            .reduce((s, sh) => s + sh.rows.filter((r) => r.edited).length, 0),
    [sheetsPayload]);

  useEffect(() => {
    if (!data || !sheetsPayload) return;
    if (!isSummary && !anyEdited) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setPreviewBusy(true);
      try {
        const { data: p } = await api.post(`${fpBase(tenantId)}/review-sheets/preview`,
                                           { period, sheets: sheetsPayload });
        setPreview(p);
      } catch { setPreview(null); }
      finally { setPreviewBusy(false); }
    }, 700);
    return () => clearTimeout(t);
  }, [sheetsPayload, isSummary, anyEdited, tenantId, period, data]);

  const setField = (key, field, value) =>
    setDraft((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${fpBase(tenantId)}/review-sheets/${area}`,
                    { period, rows });
      toast.success(`${current.label} sheet saved`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const act = async (kind) => {
    setActing(kind);
    try {
      await api.post(`${fpBase(tenantId)}/review-sheets/${area}/${kind}`, { period });
      toast.success(kind === "approve"
        ? `${current.label} approved — the model now reads this sheet`
        : `${current.label} re-opened for editing`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setActing(""); }
  };

  const discard = async () => {
    setActing("discard");
    try {
      await api.post(`${fpBase(tenantId)}/review-sheets/${area}/discard`, { period });
      toast.success(`${current.label} draft discarded — the sheet is back to the engine's reading`);
      setDraft({});
      setConfirmDiscard(false);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setActing(""); }
  };

  return (
    <FpShell title="Line-item review" showPeriod
             subtitle={`Every line the engine reads, laid out like the workbook — check, alter if needed, then approve · ${monthLabel(period)}`}>
      {!data ? (
        <div className="panel p-8 text-sm text-muted-foreground">Reading the month's lines…</div>
      ) : (
        <div className="space-y-5" data-testid="fp-line-review">
          <div className="flex flex-wrap gap-2" data-testid="fp-lr-area-tabs">
            {[...data.area_order, "summary"].map((a) => {
              const s = a === "summary" ? { label: "Summary", status: null } : data.areas[a];
              return (
                <button key={a} onClick={() => { setArea(a); setDraft({}); setBridgeOpen(false); setConfirmDiscard(false); }}
                        data-testid={`fp-lr-tab-${a}`}
                        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs transition-colors ${
                          area === a ? "border-navy bg-navy text-white"
                                     : "border-border bg-card text-navy hover:border-navy/40"}`}>
                  {s.label}
                  {s.status && (
                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${
                      area === a ? "bg-white/20 text-white" : TONE[s.status]}`}
                          data-testid={`fp-lr-status-${a}`}>
                      {s.status}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {locked && (
            <div className="flex items-start gap-3 rounded-2xl border border-success/40 bg-success/10 px-5 py-3 text-xs text-[hsl(152_60%_26%)]"
                 data-testid="fp-lr-approved-banner">
              <Lock size={15} className="mt-0.5 shrink-0" />
              <span>
                <strong>Approved</strong>
                {current.approved_by_email ? ` by ${current.approved_by_email}` : ""}
                {current.approved_at ? ` on ${new Date(current.approved_at).toLocaleString("en-IN")}` : ""}.
                {" "}The Financial Model, Explorer and every report for {monthLabel(period)} now read
                this sheet. The sheet is locked — a platform admin can re-open it.
              </span>
            </div>
          )}
          {(current?.stale_count || 0) > 0 && (
            <div className="flex items-start gap-2 rounded-2xl border border-warning/50 bg-warning/10 px-5 py-3 text-xs text-[hsl(30_88%_26%)]"
                 data-testid="fp-lr-stale-banner">
              <Warning size={15} className="mt-0.5 shrink-0" />
              {current.stale_count} saved line(s) no longer appear in the uploaded files — they are
              kept below, marked stale, so nothing reviewed is silently dropped.
            </div>
          )}

          {preview && (
            <div className="panel flex flex-wrap items-center gap-x-8 gap-y-3 px-6 py-4"
                 data-testid="fp-lr-impact-strip">
              <p className="label-caps w-full sm:w-auto">
                If approved now{previewBusy ? " · recalculating…" : ""}
              </p>
              {IMPACT_LINES.map((l) => {
                const cur = preview.current?.[l.key];
                const pro = preview.projected?.[l.key];
                const delta = (pro ?? 0) - (cur ?? 0);
                return (
                  <div key={l.key} data-testid={`fp-lr-impact-${l.key}`}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                      {l.label}
                    </p>
                    <p className="font-mono text-sm font-semibold tabular-nums text-navy">
                      {fmtLine(l, cur)}
                      <span className="mx-1.5 text-muted-foreground">→</span>
                      {fmtLine(l, pro)}
                      {!l.pct && <DeltaBadge value={delta} />}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {isSummary && (
            <div className="panel overflow-hidden">
              <div className="border-b border-border px-6 py-4">
                <h3 className="font-display text-lg text-navy">Financial performance — summary impact</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  The Level 1 chain for {monthLabel(period)}: what is published now beside what would
                  publish if every edited sheet were approved this moment. Edit any of the four
                  sheets and this summary updates live.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="fp-lr-summary-table">
                  <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                    <tr>
                      <th className="px-6 py-2.5 text-left">Line</th>
                      <th className="px-4 py-2.5 text-right">Current</th>
                      <th className="px-4 py-2.5 text-right">Projected after approval</th>
                      <th className="px-6 py-2.5 text-right">Change</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {SUMMARY_LINES.map((l) => {
                      const cur = preview?.current?.[l.key];
                      const pro = preview?.projected?.[l.key];
                      const delta = (pro ?? 0) - (cur ?? 0);
                      const moved = Math.abs(delta) >= 0.005;
                      return (
                        <tr key={l.key} data-testid={`fp-lr-summary-${l.key}`}
                            className={l.bold ? "bg-muted/20 font-semibold" : ""}>
                          <td className="px-6 py-2.5 text-navy">{l.label}</td>
                          <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
                            {preview ? fmtLine(l, cur) : previewBusy ? "…" : "—"}
                          </td>
                          <td className={`px-4 py-2.5 text-right font-mono tabular-nums ${
                            moved ? "text-terracotta" : "text-navy"}`}>
                            {preview ? fmtLine(l, pro) : previewBusy ? "…" : "—"}
                          </td>
                          <td className="px-6 py-2.5 text-right font-mono tabular-nums">
                            {preview && !l.pct && moved
                              ? <DeltaBadge value={delta} />
                              : preview && !moved ? <span className="text-muted-foreground">—</span> : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!preview && !previewBusy && (
                <div className="border-t border-border px-6 py-3 text-[11px] text-muted-foreground"
                     data-testid="fp-lr-summary-note">
                  Nothing to compare yet — the summary could not be simulated for this month.
                </div>
              )}
            </div>
          )}

          {!isSummary && current.bridge && (
            <BridgeCard bridge={current.bridge} label={current.label}
                        open={bridgeOpen} onToggle={() => setBridgeOpen((o) => !o)} />
          )}

          {!isSummary && (
          <div className="panel overflow-hidden">
            <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border px-6 py-4">
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="label-caps">Books control total</p>
                  <p className="font-mono text-sm font-semibold tabular-nums text-navy"
                     data-testid="fp-lr-control-total">{inr(current.control_total)}</p>
                </div>
                <div>
                  <p className="label-caps">P&L lines here</p>
                  <p className={`font-mono text-sm font-semibold tabular-nums ${
                    editedCount ? "text-terracotta" : "text-navy"}`}
                     data-testid="fp-lr-books-lines">{inr(booksLines)}</p>
                </div>
                <div>
                  <p className="label-caps">Register lines here</p>
                  <p className={`font-mono text-sm font-semibold tabular-nums ${
                    editedCount ? "text-terracotta" : "text-navy"}`}
                     data-testid="fp-lr-register-lines">{inr(registerLines)}</p>
                </div>
                <div>
                  <p className="label-caps">Current model</p>
                  <p className="font-mono text-sm font-semibold tabular-nums text-navy"
                     data-testid="fp-lr-model-total">{inr(current.model_total)}</p>
                </div>
                <div>
                  <p className="label-caps">Projected after approval</p>
                  <p className="font-mono text-sm font-semibold tabular-nums text-navy"
                     data-testid="fp-lr-projected-total">
                    {preview?.areas?.[area]
                      ? <>{inr(preview.areas[area].projected)}
                          <DeltaBadge value={preview.areas[area].projected
                                             - preview.areas[area].current} /></>
                      : previewBusy ? "…" : inr(current.model_total)}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api${fpBase(tenantId)}/review-sheets.xlsx?period=${period}`}
                   data-testid="fp-lr-download"
                   className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy hover:border-terracotta">
                  <DownloadSimple size={13} /> Excel
                </a>
                {data.can_reopen && locked && (
                  <button onClick={() => act("reopen")} disabled={!!acting}
                          data-testid="fp-lr-reopen"
                          className="inline-flex items-center gap-2 rounded-full border border-warning/60 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(30_88%_30%)] hover:bg-warning/10 disabled:opacity-50">
                    <LockOpen size={13} /> {acting === "reopen" ? "Re-opening…" : "Re-open"}
                  </button>
                )}
                {canEdit && (current?.version || 0) > 0 && (
                  confirmDiscard ? (
                    <>
                      <button onClick={discard} disabled={!!acting}
                              data-testid="fp-lr-discard-confirm"
                              className="rounded-full bg-terracotta px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-white hover:bg-terracotta/90 disabled:opacity-50">
                        {acting === "discard" ? "Discarding…" : "Confirm discard"}
                      </button>
                      <button onClick={() => setConfirmDiscard(false)}
                              data-testid="fp-lr-discard-cancel"
                              className="rounded-full border border-border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-navy hover:border-navy/40">
                        Keep draft
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDiscard(true)}
                            data-testid="fp-lr-discard"
                            className="rounded-full border border-terracotta/50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-terracotta hover:bg-terracotta/10">
                      Discard draft
                    </button>
                  )
                )}
                {canEdit && (
                  <button onClick={save} disabled={saving || !editedCount}
                          data-testid="fp-lr-save"
                          className="rounded-full border border-terracotta px-5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-terracotta hover:bg-terracotta/10 disabled:opacity-50">
                    {saving ? "Saving…" : `Save sheet${editedCount ? ` (${editedCount} edited)` : ""}`}
                  </button>
                )}
                {data.can_approve && !locked && !isSummary && (
                  <button onClick={() => act("approve")} disabled={!!acting}
                          data-testid="fp-lr-approve"
                          className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2 text-[10px] font-bold uppercase tracking-[0.12em] text-white hover:bg-navy/90 disabled:opacity-50">
                    <CheckCircle size={13} /> {acting === "approve" ? "Approving…" : "Approve sheet"}
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="fp-lr-grid">
                <thead className="bg-muted/40 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2.5 text-left">In</th>
                    <th className="px-3 py-2.5 text-left">Line</th>
                    <th className="px-3 py-2.5 text-left">Source</th>
                    <th className="px-3 py-2.5 text-right">Books amount</th>
                    <th className="px-3 py-2.5 text-right">Sheet amount</th>
                    <th className="px-3 py-2.5 text-left">Area</th>
                    <th className="px-3 py-2.5 text-left">Component</th>
                    <th className="px-3 py-2.5 text-left">{area === "dvc" ? "Outlet" : "Category"}</th>
                    {area === "fixed_cost" && <th className="px-3 py-2.5 text-left">Function</th>}
                    <th className="px-3 py-2.5 text-left">Control item</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {grouped.map((g) => {
                    const shut = collapsed[`${area}:${g.key}`];
                    return (
                      <Fragment key={g.key}>
                        <tr className="bg-muted/30" data-testid={`fp-lr-stage-${g.key}`}>
                          <td colSpan={area === "fixed_cost" ? 10 : 9} className="px-3 py-2">
                            <button onClick={() => setCollapsed((c) => ({ ...c, [`${area}:${g.key}`]: !shut }))}
                                    data-testid={`fp-lr-stage-toggle-${g.key}`}
                                    className="flex w-full items-center justify-between gap-3 text-left">
                              <span className="flex items-center gap-2">
                                <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-navy">
                                  {g.label}
                                </span>
                                <span className="rounded-full bg-card px-2 py-0.5 text-[9px] text-muted-foreground">
                                  {g.rows.length} {g.rows.length === 1 ? "line" : "lines"}
                                </span>
                                {g.sign === "−" && (
                                  <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-bold text-navy">
                                    deducted
                                  </span>
                                )}
                                {g.sign === "0" && (
                                  <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[9px] font-bold text-navy">
                                    ignored
                                  </span>
                                )}
                              </span>
                              <span className="flex items-center gap-3">
                                {g.model_amount != null && (
                                  <span className="font-mono text-[10px] tabular-nums text-muted-foreground"
                                        data-testid={`fp-lr-stage-model-${g.key}`}>
                                    model {inr(g.model_amount)}
                                  </span>
                                )}
                                <span className="font-mono text-[11px] font-semibold tabular-nums text-navy"
                                      data-testid={`fp-lr-stage-total-${g.key}`}>
                                  {g.sign === "−" ? `(${inr(g.total)})` : inr(g.total)}
                                </span>
                                <span className="text-[9px] uppercase tracking-[0.1em] text-terracotta">
                                  {shut ? "Show" : "Hide"}
                                </span>
                              </span>
                            </button>
                          </td>
                        </tr>
                        {!shut && g.rows.map((r) => (
                    <tr key={r.key} data-testid={`fp-lr-row-${r.key}`}
                        className={`${r.stale ? "bg-warning/10" : r.edited ? "bg-terracotta/5" : ""} ${
                          r.excluded ? "opacity-50" : ""}`}>
                        <td className="px-3 py-1.5">
                          <input type="checkbox" checked={!r.excluded} disabled={!canEdit || r.derived}
                                 data-testid={`fp-lr-include-${r.key}`}
                                 onChange={(e) => setField(r.key, "excluded", !e.target.checked)}
                                 className="h-3.5 w-3.5 accent-[hsl(14_70%_47%)]" />
                        </td>
                        <td className="px-3 py-1.5 text-navy">
                          <div>{r.name}{r.stale && <span className="ml-2 rounded-full bg-warning/20 px-2 py-0.5 text-[9px] font-bold uppercase text-[hsl(30_88%_30%)]">stale</span>}{r.derived && <span className="ml-2 rounded-full bg-accent px-2 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">apportioned</span>}</div>
                          {r.path && <div className="text-[10px] text-muted-foreground">{r.path}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-[10px] text-muted-foreground">{r.source_label}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums text-muted-foreground">
                          {inr(r.tally_amount)}
                        </td>
                        <td className="px-3 py-1.5 w-32">
                          <NumCell value={r.amount} disabled={!canEdit || r.excluded || r.derived}
                                   testid={`fp-lr-amount-${r.key}`}
                                   onChange={(v) => setField(r.key, "amount", v === "" ? 0 : v)} />
                        </td>
                        <td className="px-3 py-1.5 w-44">
                          <select value={r.excluded ? "excluded" : r.area} disabled={!canEdit || r.derived}
                                  data-testid={`fp-lr-area-${r.key}`}
                                  onChange={(e) => {
                                    if (e.target.value === "excluded") setField(r.key, "excluded", true);
                                    else setDraft((d) => ({ ...d, [r.key]: { ...(d[r.key] || {}), area: e.target.value, excluded: false } }));
                                  }}
                                  className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-xs text-navy outline-none hover:border-border focus:border-terracotta focus:bg-card disabled:opacity-70">
                            {data.area_options.map((o) => (
                              <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-1.5 w-40">
                          <Cell value={r.component} disabled={!canEdit || r.excluded || r.derived}
                                testid={`fp-lr-component-${r.key}`}
                                onChange={(v) => setField(r.key, "component", v)} />
                        </td>
                        <td className="px-3 py-1.5 w-40">
                          <Cell value={area === "dvc" ? r.channel : r.category}
                                disabled={!canEdit || r.excluded || r.derived}
                                testid={`fp-lr-slot-${r.key}`}
                                onChange={(v) => setField(r.key, area === "dvc" ? "channel" : "category", v)} />
                        </td>
                        {area === "fixed_cost" && (
                          <td className="px-3 py-1.5 w-40">
                            <FnCell value={r.function} options={data.function_options || []}
                                    disabled={!canEdit || r.excluded || r.derived}
                                    testid={`fp-lr-function-${r.key}`}
                                    onChange={(v) => setField(r.key, "function", v)} />
                          </td>
                        )}
                        <td className="px-3 py-1.5 w-40">
                          <Cell value={r.control_item} disabled={!canEdit || r.excluded || r.detail || r.derived}
                                testid={`fp-lr-control-${r.key}`}
                                onChange={(v) => setField(r.key, "control_item", v)} />
                        </td>
                      </tr>
                  ))}
                      </Fragment>
                    );
                  })}
                  {!rows.length && (
                    <tr><td colSpan={10} className="px-6 py-10 text-center text-muted-foreground"
                            data-testid="fp-lr-empty">
                      No lines are routed to {current.label.toLowerCase()} for {monthLabel(period)} yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border-t border-border px-6 py-3 text-[11px] text-muted-foreground">
              Lines are grouped into the step of the calculation they belong to — opening stock in,
              purchases, operating ledgers, register detail, closing stock out — so the grid reads
              the way the figure is actually built. Tap <em>Explain</em> above to see the formula
              and the stock bridges. Untick a line to set it aside (inter-entity, owner's own,
              duplicates). Change an amount or a classification and the <em>Projected</em> figures
              move, then Save. Approving freezes the
              sheet and the whole engine — Financial Model, Explorer, Level 1-3, budget vs actual —
              reads it from that moment.
            </div>
          </div>
          )}
        </div>
      )}
    </FpShell>
  );
}
